import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../ui/dropdown-menu';
import { Plus, Trash2, Edit2, User, ChevronDown, ChevronRight } from 'lucide-react';
import { useSupabase } from '../../../contexts/SupabaseContext';
import OptionCard from './OptionCard';
import { Calendar } from '../../ui/calendar';
import { format } from 'date-fns';

interface DecisionNodeProps {
  decision: any;
  depth: number;
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const DecisionNode: React.FC<DecisionNodeProps> = ({ decision, depth, expanded, setExpanded }) => {
  const {
    decisions,
    decisionOptions,
    loadDecisionOptions,
    createDecision,
    updateDecision,
    deleteDecision,
    createDecisionOption,
    updateDecisionOption,
    deleteDecisionOption,
    decisionOptionPoints,
    loadDecisionOptionPoints,
    createDecisionOptionPoint,
    updateDecisionOptionPoint,
    deleteDecisionOptionPoint,
  } = useSupabase();

  const isExpanded = expanded[decision.id] ?? true;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(decision.title);
  const [newSubTitle, setNewSubTitle] = useState('');
  const [newOptionText, setNewOptionText] = useState('');
  const [owner, setOwner] = useState(decision.owner || '');
  const [dueDate, setDueDate] = useState<string | null>(decision.due_date || null as any);
  const [description, setDescription] = useState(decision.description || '');

  useEffect(() => {
    loadDecisionOptions(decision.id);
  }, [decision.id, loadDecisionOptions]);

  const getChildren = (parentId: string | null) => decisions.filter((d) => d.parent_id === parentId);
  const options = decisionOptions[decision.id] || [];
  const children = getChildren(decision.id);

  return (
    <div className={`ml-${depth * 4} mb-4`}>
      <Card className="shadow-md">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setExpanded((prev) => ({ ...prev, [decision.id]: !isExpanded }))}
              className="mr-2"
            >
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </Button>
            {editing ? (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={async () => {
                  try {
                    await updateDecision(decision.id, { title });
                    setEditing(false);
                  } catch (error: any) {
                    console.error(error.message || JSON.stringify(error));
                    alert(error.message || 'An error occurred');
                  }
                }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    try {
                      await updateDecision(decision.id, { title });
                      setEditing(false);
                    } catch (error: any) {
                      console.error(error.message || JSON.stringify(error));
                      alert(error.message || 'An error occurred');
                    }
                  }
                }}
                className="font-semibold text-base"
                autoFocus
              />
            ) : (
              <span className="font-semibold text-base" onDoubleClick={() => setEditing(true)}>{decision.title}</span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon"><Edit2 size={16} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setEditing(true)}>Edit</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => deleteDecision(decision.id)} className="text-destructive">Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Owner input */}
            <div className="ml-auto flex items-center gap-1 text-xs">
              <User size={14} />
              <Input
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                onBlur={async () => {
                  if (owner !== (decision.owner || '')) {
                    await updateDecision(decision.id, { owner });
                  }
                }}
                placeholder="Owner"
                className="w-24 text-xs"
              />
            </div>
          </div>
          {/* Description */}
          <Input
            value={description}
            onChange={(e)=>setDescription(e.target.value)}
            onBlur={async ()=>{ if(description!== (decision.description||'')){ await updateDecision(decision.id,{ description}); }}}
            placeholder="Description..."
            className="text-sm"
          />
          {/* Due date */}
          <div className="flex items-center gap-2 text-xs">
            <span>Due:</span>
            <Input
              type="date"
              value={dueDate? format(new Date(dueDate), 'yyyy-MM-dd') : ''}
              onChange={async (e)=>{
                 setDueDate(e.target.value);
                 await updateDecision(decision.id,{ due_date: e.target.value});
              }}
              className="w-36"
            />
          </div>
          {isExpanded && (
            <div className="space-y-2">
              {/* Sub-decision input */}
              <div className="flex gap-2 items-center">
                <Input
                  value={newSubTitle}
                  onChange={(e) => setNewSubTitle(e.target.value)}
                  placeholder="Add sub-decision..."
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newSubTitle.trim()) {
                      try {
                        await createDecision({ title: newSubTitle, parent_id: decision.id });
                        setNewSubTitle('');
                      } catch (error: any) {
                        console.error(error.message || JSON.stringify(error));
                        alert(error.message || 'An error occurred');
                      }
                    }
                  }}
                  className="w-48"
                />
                <Button size="sm" onClick={async () => {
                  if (newSubTitle.trim()) {
                    try {
                      await createDecision({ title: newSubTitle, parent_id: decision.id });
                      setNewSubTitle('');
                    } catch (error: any) {
                      console.error(error.message || JSON.stringify(error));
                      alert(error.message || 'An error occurred');
                    }
                  }
                }}><Plus size={14} /></Button>
              </div>
              {/* Option input */}
              <div className="flex gap-2 items-center">
                <Input
                  value={newOptionText}
                  onChange={(e) => setNewOptionText(e.target.value)}
                  placeholder="Add option..."
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newOptionText.trim()) {
                      try {
                        await createDecisionOption({ decision_id: decision.id, text: newOptionText });
                        setNewOptionText('');
                      } catch (error: any) {
                        console.error(error.message || JSON.stringify(error));
                        alert(error.message || 'An error occurred');
                      }
                    }
                  }}
                  className="w-48"
                />
                <Button size="sm" onClick={async () => {
                  if (newOptionText.trim()) {
                    try {
                      await createDecisionOption({ decision_id: decision.id, text: newOptionText });
                      setNewOptionText('');
                    } catch (error: any) {
                      console.error(error.message || JSON.stringify(error));
                      alert(error.message || 'An error occurred');
                    }
                  }
                }}><Plus size={14} /></Button>
              </div>
              {/* Option cards */}
              <div className="grid gap-2">
                {options.map((opt: any) => <OptionCard key={opt.id} option={opt} />)}
              </div>
              {/* Recursive children */}
              <div className="ml-4 border-l border-muted-foreground/20 pl-4">
                {children.map((child) => (
                  <DecisionNode
                    key={child.id}
                    decision={child}
                    depth={depth + 1}
                    expanded={expanded}
                    setExpanded={setExpanded}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DecisionNode; 